// Mercapoli - Historial de Ventas del Comercio
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MerchantHeader from '../../components/merchant/MerchantHeader';
import { 
  ChevronLeft, Calendar, TrendingUp, TrendingDown, 
  DollarSign, Package, Filter, Download
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { comercio as comercioApi } from '../../lib/api';
import { toast } from 'sonner';


const MerchantVentas = () => {
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [periodo, setPeriodo] = useState('semana');
  const [resumen, setResumen] = useState(null);
  const [detalleVentas, setDetalleVentas] = useState([]);
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [showFiltros, setShowFiltros] = useState(false);

  useEffect(() => {
    loadResumen();
  }, [periodo]);

  useEffect(() => {
    if (fechaInicio && fechaFin) {
      loadDetalle();
    }
  }, [fechaInicio, fechaFin]);

  const loadResumen = async () => {
    try {
      const data = await comercioApi.getResumenVentas(periodo);
      setResumen(data);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar resumen');
    }
    setLoading(false);
  };

  const loadDetalle = async () => {
    try {
      const data = await comercioApi.getDetalleVentas(fechaInicio, fechaFin);
      setDetalleVentas(data.pedidos || []);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar detalle');
    }
  };

  const formatMoney = (amount) => {
    const simbolo = resumen?.simbolo || 'Q';
    return `${simbolo}${(amount || 0).toLocaleString('es-GT', { minimumFractionDigits: 2 })}`;
  };

  const formatFecha = (fecha) => {
    if (!fecha) return '';
    return new Date(fecha).toLocaleDateString('es-GT', { 
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      entregado: 'bg-green-100 text-green-800',
      cancelado: 'bg-red-100 text-red-800',
      pendiente: 'bg-yellow-100 text-yellow-800',
    };
    return colors[status] || 'bg-gray-100';
  };

  // Set default dates for detail view
  useEffect(() => {
    const today = new Date();
    const lastWeek = new Date(today);
    lastWeek.setDate(today.getDate() - 7);
    
    setFechaFin(today.toISOString().split('T')[0]);
    setFechaInicio(lastWeek.toISOString().split('T')[0]);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header con Logout */}
      <MerchantHeader 
        title="Ventas" 
        rightContent={
          <Button variant="outline" size="sm" onClick={() => setShowFiltros(!showFiltros)}>
            <Filter className="h-4 w-4 mr-1" /> Filtros
          </Button>
        }
      />

      <main className="container mx-auto px-4 py-6 max-w-5xl">
        {/* Periodo Selector */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {['hoy', 'semana', 'mes'].map((p) => (
            <Button
              key={p}
              variant={periodo === p ? 'default' : 'outline'}
              onClick={() => setPeriodo(p)}
            >
              {p === 'hoy' ? 'Hoy' : p === 'semana' ? 'Esta Semana' : 'Este Mes'}
            </Button>
          ))}
        </div>

        {/* Resumen Cards */}
        {resumen && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Card className="bg-gradient-to-br from-green-500 to-emerald-600 text-white">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm opacity-80">Ventas Brutas</p>
                    <p className="text-2xl font-bold">{formatMoney(resumen.total_ventas)}</p>
                  </div>
                  <DollarSign className="h-8 w-8 opacity-50" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Pedidos</p>
                    <p className="text-2xl font-bold">{resumen.total_pedidos}</p>
                  </div>
                  <Package className="h-8 w-8 text-brand-primary opacity-50" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Comisión ({resumen.comision_porcentaje}%)</p>
                    <p className="text-2xl font-bold text-red-500">-{formatMoney(resumen.comision_total)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-brand-primary text-white">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm opacity-80">Neto Estimado</p>
                    <p className="text-2xl font-bold">{formatMoney(resumen.neto_estimado)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Comparación */}
        {resumen && resumen.comparacion_periodo_anterior !== 0 && (
          <Card className="mb-6">
            <CardContent className="p-4">
              <div className="flex items-center justify-center gap-3">
                {resumen.comparacion_periodo_anterior > 0 ? (
                  <>
                    <TrendingUp className="h-6 w-6 text-green-500" />
                    <span className="text-lg font-medium text-green-500">
                      +{resumen.comparacion_periodo_anterior.toFixed(1)}%
                    </span>
                  </>
                ) : (
                  <>
                    <TrendingDown className="h-6 w-6 text-red-500" />
                    <span className="text-lg font-medium text-red-500">
                      {resumen.comparacion_periodo_anterior.toFixed(1)}%
                    </span>
                  </>
                )}
                <span className="text-muted-foreground">comparado con el periodo anterior</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filtros de fecha */}
        {showFiltros && (
          <Card className="mb-6">
            <CardContent className="p-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Fecha Inicio</Label>
                  <Input
                    type="date"
                    value={fechaInicio}
                    onChange={(e) => setFechaInicio(e.target.value)}
                  />
                </div>
                <div>
                  <Label>Fecha Fin</Label>
                  <Input
                    type="date"
                    value={fechaFin}
                    onChange={(e) => setFechaFin(e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Detalle de Ventas */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Detalle de Pedidos</span>
              <Badge variant="outline">{detalleVentas.length} pedidos</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {detalleVentas.length === 0 ? (
              <div className="text-center py-8">
                <Package className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">No hay pedidos en este periodo</p>
              </div>
            ) : (
              <div className="space-y-3">
                {detalleVentas.map((pedido) => (
                  <div
                    key={pedido.pedido_id}
                    className="flex items-center justify-between p-3 bg-muted rounded-lg"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium">#{pedido.pedido_id?.slice(-6)}</p>
                        <Badge className={getStatusColor(pedido.estado)}>{pedido.estado}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">{formatFecha(pedido.created_at)}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {pedido.items?.length || 0} items
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold">{formatMoney(pedido.subtotal || pedido.total)}</p>
                      {pedido.comision && (
                        <p className="text-xs text-red-500">-{formatMoney(pedido.comision)}</p>
                      )}
                      {pedido.neto && (
                        <p className="text-xs text-green-600 font-medium">{formatMoney(pedido.neto)}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default MerchantVentas;
